import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getGymNumbers } from "@/lib/api";
import { numberLocale } from "@/lib/money";

// Gym numbers (backend routes/gym_numbers.py): busy and quiet hours from check-ins, revenue by plan with
// discounts against the catalog, upgrade and renewal rates, and where new members came from with the cost
// per join. One gym at a time (the gym switcher), for the last 1, 3, 6 or 12 months.

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SOURCE_LABEL = { ads: "Ads", website: "Website", booked: "Booked online", walk_in: "Walk-in", referral: "Referral", unknown: "Not known" };
const fmt = (n) => new Intl.NumberFormat(numberLocale()).format(Math.round(n || 0));
const money = (n) => (n == null ? "–" : `${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmt(n)}`);
const hourLabel = (h) => `${((h + 11) % 12) + 1}${h < 12 ? "am" : "pm"}`;

function Tile({ label, value, sub }) {
  return (
    <div className="card-surface p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="font-display text-2xl font-extrabold mt-1 text-zinc-100 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, sub, children }) {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="font-display text-xl font-bold">{title}</h3>
        {sub && <p className="text-sm text-zinc-400">{sub}</p>}
      </div>
      {children}
    </section>
  );
}

// One hue, light to dark, for "how many people": the busiest hour is the darkest green.
function Heatmap({ cells }) {
  const hours = useMemo(() => {
    const hs = [...new Set(cells.map((c) => c.hour))].sort((a, b) => a - b);
    if (!hs.length) return [];
    const out = [];
    for (let h = hs[0]; h <= hs[hs.length - 1]; h++) out.push(h);
    return out;
  }, [cells]);
  const max = Math.max(1, ...cells.map((c) => c.avg));
  const at = (d, h) => cells.find((c) => c.day === d && c.hour === h)?.avg || 0;
  if (!hours.length) return <div className="card-surface p-6 text-sm text-zinc-400">No check-ins with times yet.</div>;
  return (
    <div className="card-surface p-4 overflow-x-auto" data-testid="numbers-heatmap">
      <table className="text-[11px] border-separate" style={{ borderSpacing: 2 }}>
        <thead>
          <tr>
            <th />
            {hours.map((h) => <th key={h} className="font-normal text-zinc-500 px-0.5 w-9">{hourLabel(h)}</th>)}
          </tr>
        </thead>
        <tbody>
          {DAYS.map((d, di) => (
            <tr key={d}>
              <td className="text-zinc-500 pr-2">{d}</td>
              {hours.map((h) => {
                const v = at(di, h);
                const a = v ? 0.12 + 0.88 * (v / max) : 0;
                return (
                  <td key={h} title={`${d} ${hourLabel(h)}: ${v} check-ins on average`}
                    className="h-7 w-9 rounded text-center tabular-nums"
                    style={{ background: v ? `rgba(16,185,129,${a})` : "#151515", color: a > 0.55 ? "#022c22" : "#a1a1aa" }}>
                    {v ? (v >= 10 ? Math.round(v) : v.toFixed(1)) : ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-zinc-500 mt-2">Average check-ins in each hour over the last 4 weeks. Hover a square for the exact number.</p>
    </div>
  );
}

const pct = (r) => (r == null ? "–" : `${r}%`);

function CampaignRow({ name, row, depth, open, onToggle }) {
  const pad = ["pl-4", "pl-9", "pl-14"][depth];
  return (
    <tr className={`border-b border-[#1a1a1a] ${depth ? "bg-[#0c0c0c]" : ""}`}>
      <td className={`${pad} pr-4 py-2 text-zinc-100`}>
        {onToggle ? (
          <button type="button" onClick={onToggle} className="text-left hover:underline">
            <span className="text-zinc-500 mr-1.5">{open ? "▾" : "▸"}</span>{name}
          </button>
        ) : <span className={depth === 2 ? "text-zinc-300" : ""}>{name}</span>}
      </td>
      <td className="px-4 py-2 text-right tabular-nums">{fmt(row.leads)}</td>
      <td className="px-4 py-2 text-right tabular-nums">{fmt(row.joins)}</td>
      <td className="px-4 py-2 text-right tabular-nums text-zinc-300">{pct(row.rate)}</td>
    </tr>
  );
}

function Campaigns({ c }) {
  const [open, setOpen] = useState({});
  const flip = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const rows = [];
  c.campaigns.forEach((cp) => {
    const ck = `c:${cp.campaign}`;
    rows.push(<CampaignRow key={ck} name={cp.campaign} row={cp} depth={0} open={open[ck]} onToggle={cp.adsets.length ? () => flip(ck) : null} />);
    if (!open[ck]) return;
    cp.adsets.forEach((as) => {
      const ak = `${ck}|${as.adset}`;
      rows.push(<CampaignRow key={ak} name={as.adset} row={as} depth={1} open={open[ak]} onToggle={as.ads.length ? () => flip(ak) : null} />);
      if (open[ak]) as.ads.forEach((ad) => rows.push(<CampaignRow key={`${ak}|${ad.ad}`} name={ad.ad} row={ad} depth={2} />));
    });
  });
  return (
    <div className="space-y-3">
      {!c.complete && (
        <p className="text-xs text-amber-200/80" data-testid="numbers-campaigns-filling">
          Ad details are still being read from GoHighLevel: {fmt(c.leads_with_ad_data)} of {fmt(c.leads_total)} leads in this period so far.
          The numbers fill in over the next few hours.
        </p>
      )}
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm" data-testid="numbers-campaigns">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f] bg-[#0d0d0d]">
              <th className="px-4 py-3 font-medium">Campaign, ad set, ad</th>
              <th className="px-4 py-3 font-medium text-right">Leads</th>
              <th className="px-4 py-3 font-medium text-right">New members</th>
              <th className="px-4 py-3 font-medium text-right">Lead to member</th>
            </tr>
          </thead>
          <tbody>
            {rows}
            {!c.campaigns.length && <tr><td colSpan={4} className="px-4 py-6 text-zinc-500">No leads with ad details in this period yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {c.best_ads.length > 0 && (
        <div className="card-surface overflow-x-auto">
          <div className="px-4 pt-3 text-xs uppercase tracking-[0.18em] text-zinc-500">Best ads (10 or more leads)</div>
          <table className="w-full text-sm mt-1" data-testid="numbers-best-ads">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
                <th className="px-4 py-2 font-medium">Ad</th>
                <th className="px-4 py-2 font-medium">Campaign</th>
                <th className="px-4 py-2 font-medium text-right">Leads</th>
                <th className="px-4 py-2 font-medium text-right">New members</th>
                <th className="px-4 py-2 font-medium text-right">Lead to member</th>
              </tr>
            </thead>
            <tbody>
              {c.best_ads.map((a) => (
                <tr key={`${a.campaign}|${a.adset}|${a.ad}`} className="border-b border-[#1a1a1a] last:border-0">
                  <td className="px-4 py-2 text-zinc-100">{a.ad}</td>
                  <td className="px-4 py-2 text-zinc-400">{a.campaign}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmt(a.leads)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmt(a.joins)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-zinc-300">{pct(a.rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Bar({ value, max, label, right }) {
  const pct = max ? Math.max(2, (100 * value) / max) : 0;
  return (
    <div className="flex items-center gap-3 text-sm" title={`${label}: ${right}`}>
      <div className="w-40 shrink-0 text-zinc-300 truncate">{label}</div>
      <div className="flex-1 h-4 bg-[#151515] rounded-r">
        <div className="h-4 rounded-r bg-emerald-500/80" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-28 shrink-0 text-right text-zinc-300 tabular-nums">{right}</div>
    </div>
  );
}

export default function GymNumbers() {
  const { activeFacility } = useAuth();
  const [months, setMonths] = useState(3);
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    getGymNumbers(months).then(setData).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load the numbers"));
  }, [months, activeFacility?.id]);

  const slots = data?.hours?.slots || [];
  const withPeople = slots.filter((s) => s.avg > 0);
  const quiet = [...slots].sort((a, b) => a.avg - b.avg).slice(0, 5);
  const plans = data?.plans?.plans || [];
  const src = data?.marketing?.by_source || {};
  const srcMax = Math.max(0, ...Object.values(src));

  return (
    <div className="space-y-8" data-testid="gym-numbers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Gym numbers · {activeFacility?.name || "this gym"}</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Gym numbers</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-3xl">Busy hours, what sells, who comes back for more, and where new members come from.</p>
        </div>
        <div className="flex items-center gap-1 card-surface p-1 rounded-full">
          {[1, 3, 6, 12].map((m) => (
            <button key={m} type="button" onClick={() => setMonths(m)} data-testid={`numbers-months-${m}`}
              className={`px-3 h-8 rounded-full text-xs font-medium ${months === m ? "bg-zinc-100 text-zinc-900" : "text-zinc-400"}`}>
              {m === 1 ? "This month" : `${m} months`}
            </button>
          ))}
        </div>
      </div>

      {!data && <div className="text-zinc-500 text-sm inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading</div>}

      {data && (
        <>
          <Section title="Busy and quiet hours" sub={`From ${data.hours.checkins} check-ins in the last 4 weeks.`}>
            <Heatmap cells={data.hours.cells} />
            {withPeople.length > 0 && (
              <div className="grid md:grid-cols-2 gap-3">
                <div className="card-surface p-4 space-y-2">
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Fullest sessions</div>
                  {slots.slice(0, 5).map((s) => <Bar key={s.id} label={`${DAYS[s.day]} ${s.time}`} value={s.avg} max={slots[0]?.avg} right={`${s.avg} a session`} />)}
                </div>
                <div className="card-surface p-4 space-y-2">
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Emptiest sessions</div>
                  {quiet.map((s) => <Bar key={s.id} label={`${DAYS[s.day]} ${s.time}`} value={s.avg} max={slots[0]?.avg} right={`${s.avg} a session`} />)}
                </div>
              </div>
            )}
          </Section>

          <Section title="What sells" sub={`Memberships sold ${new Date(data.from).toLocaleDateString(undefined, { day: "numeric", month: "short" })} to today. Revenue and prices are before tax.`}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Tile label="Revenue" value={money(data.plans.revenue)} />
              <Tile label="Memberships sold" value={fmt(data.plans.sold)} />
              <Tile label="Sold below list price" value={fmt(data.plans.below_list)}
                sub={data.plans.sold ? `${Math.round((100 * data.plans.below_list) / data.plans.sold)}% of sales` : ""} />
            </div>
            <div className="card-surface overflow-x-auto">
              <table className="w-full text-sm" data-testid="numbers-plans">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f] bg-[#0d0d0d]">
                    <th className="px-4 py-3 font-medium">Plan</th>
                    <th className="px-4 py-3 font-medium text-right">Sold</th>
                    <th className="px-4 py-3 font-medium text-right">Revenue</th>
                    <th className="px-4 py-3 font-medium text-right">Share</th>
                    <th className="px-4 py-3 font-medium text-right">Average price</th>
                    <th className="px-4 py-3 font-medium text-right">List price</th>
                    <th className="px-4 py-3 font-medium text-right">Below list</th>
                    <th className="px-4 py-3 font-medium text-right">Average discount</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p) => (
                    <tr key={p.plan} className="border-b border-[#1a1a1a] last:border-0">
                      <td className="px-4 py-2.5 text-zinc-100">{p.plan}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{p.sold}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{money(p.revenue)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-zinc-400">{p.share}%</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{money(p.avg_price)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-zinc-400">{p.list_price ? money(p.list_price) : "not in catalog"}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{p.below_list}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-zinc-400">{p.below_list ? money(p.avg_discount) : "–"}</td>
                    </tr>
                  ))}
                  {!plans.length && <tr><td colSpan={8} className="px-4 py-6 text-zinc-500">No memberships sold in this period.</td></tr>}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Who comes back for more" sub={`Memberships that ended in this period, and how many bought again within ${data.upgrades.window_days} days.`}>
            <div className="grid grid-cols-2 gap-3">
              <Tile label="6 Week Challenge finishers who bought again" value={data.upgrades.six_week.rate == null ? "–" : `${data.upgrades.six_week.rate}%`}
                sub={`${data.upgrades.six_week.bought} of ${data.upgrades.six_week.ended}`} />
              <Tile label="Other members who renewed" value={data.upgrades.others.rate == null ? "–" : `${data.upgrades.others.rate}%`}
                sub={`${data.upgrades.others.bought} of ${data.upgrades.others.ended} (day and week passes left out)`} />
            </div>
          </Section>

          <Section title="Where new members come from" sub="New members (consults sold) by where they first came from, and what ads cost per join.">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile label="New members" value={fmt(data.marketing.joins)} />
              <Tile label="Leads" value={fmt(data.marketing.leads.total)} sub={data.marketing.lead_to_join != null ? `${data.marketing.lead_to_join}% became members` : ""} />
              <Tile label="Ad spend" value={money(data.marketing.ad_spend)} sub="Ad Spend expenses" />
              <Tile label="Ad cost per ads join" value={money(data.marketing.cost_per_ads_join)}
                sub={data.marketing.cost_per_join ? `${money(data.marketing.cost_per_join)} per join overall`
                  : data.marketing.ad_spend ? "No new members from ads yet" : "Add Ad Spend in Expenses"} />
            </div>
            <div className="card-surface p-4 space-y-2" data-testid="numbers-sources">
              {Object.entries(src).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                <Bar key={k} label={SOURCE_LABEL[k] || k} value={n} max={srcMax} right={`${n} (${Math.round((100 * n) / Math.max(1, data.marketing.joins))}%)`} />
              ))}
              {!data.marketing.joins && <div className="text-sm text-zinc-500">No new members in this period.</div>}
            </div>
            <p className="text-xs text-zinc-500">"Not known" means FPA has no lead, questionnaire or referral for that member.</p>
          </Section>

          {data.marketing.campaigns && (
            <Section title="Joins per ad campaign"
              sub="Leads and new members by the Meta campaign, ad set and ad each lead first came from. A new member counts for the ad of their first lead here. Tap a campaign or ad set to open it.">
              <Campaigns c={data.marketing.campaigns} />
            </Section>
          )}
        </>
      )}
    </div>
  );
}
